export const parsePriceWithAmount = (price = '0', amount = 1): number => {
    const trimmedPrice = price.trim();
    let parsedPrice = parseFloat(trimmedPrice);
    if (
        isNaN(parsedPrice) &&
        trimmedPrice.length > 0 &&
        !/^\d/.test(trimmedPrice)
    ) {
        parsedPrice = parseFloat(trimmedPrice.slice(1));
    }
    if (isNaN(parsedPrice)) return 0;

    // Convert to integers to avoid floating point precision issues
    const priceCents = Math.round(parsedPrice * 10000);
    const resultCents = priceCents * amount;
    // Convert back to dollars
    const result = resultCents / 10000;

    return result;
};

type CurrencyConfig = {
    symbol: string;
    after: boolean;
    decimalPlaces: number;
    decimalSeparator: string;
    thousandSeparator: string;
    freeShippingThreshold?: number;
};

const countryCodeToCurrency: Record<string, string> = {
    NO: 'NOK',
    US: 'USD',
    GB: 'GBP',
    SE: 'SEK',
    DE: 'EUR',
    FR: 'EUR',
    ES: 'EUR',
    IT: 'EUR',
    NL: 'EUR',
    BE: 'EUR',
    AT: 'EUR',
    PT: 'EUR',
    IE: 'EUR',
    FI: 'EUR',
    LU: 'EUR',
    GR: 'EUR',
    SI: 'EUR',
    SK: 'EUR',
    EE: 'EUR',
    LV: 'EUR',
    LT: 'EUR',
    MT: 'EUR',
    CY: 'EUR',
    HR: 'EUR',
    DK: 'DKK',
    CA: 'CAD',
    AU: 'AUD',
    NZ: 'NZD',
    IS: 'ISK',
};

export const InternationalFreeShippingThreshold = 1500; //In NOK

/**
 * The currency config for the different currencies.
 *  Note that this is not complete, but only includes the currencies that are supported by
 *  our shop and needs to be updated when new currencies are added.
 */
export const currencyConfig: Record<string, CurrencyConfig> = {
    NOK: {
        symbol: 'kr',
        after: true,
        decimalPlaces: 0,
        decimalSeparator: ',',
        thousandSeparator: ' ',
        freeShippingThreshold: 799,
    },
    USD: {
        symbol: '$',
        after: false,
        decimalPlaces: 2,
        decimalSeparator: '.',
        thousandSeparator: ',',
        freeShippingThreshold: 129,
    },
    GBP: {
        symbol: '£',
        after: false,
        decimalPlaces: 2,
        decimalSeparator: '.',
        thousandSeparator: ',',
        freeShippingThreshold: 100,
    },
    EUR: {
        symbol: '€',
        after: false,
        decimalPlaces: 2,
        decimalSeparator: ',',
        thousandSeparator: ' ',
        freeShippingThreshold: 110,
    },
    SEK: {
        symbol: 'kr',
        after: true,
        decimalPlaces: 0,
        decimalSeparator: ',',
        thousandSeparator: ' ',
    },
    DKK: {
        symbol: 'kr',
        after: true,
        decimalPlaces: 0,
        decimalSeparator: ',',
        thousandSeparator: ' ',
    },
    ISK: {
        symbol: 'kr',
        after: true,
        decimalPlaces: 0,
        decimalSeparator: ',',
        thousandSeparator: ' ',
    },
};

/**
 * Get the currency config for a given currency.
 * @param currency - The currency to get the config for.
 * @returns The currency config. If the currency is not found, it will return the USD config.
 */
export const getCurrencyConfig: (currency: string) => CurrencyConfig = (
    currency: string,
) => {
    let config: CurrencyConfig | undefined =
        currencyConfig[currency as keyof typeof currencyConfig];
    if (!config) {
        //Search for config in currencyConfig
        config = Object.values(currencyConfig).find(
            (c) => c.symbol === currency,
        );
        if (!config) {
            // If no config is found, Fallback to USD
            return currencyConfig.USD;
        }
    }
    return config;
};

/**
 * Get the currency from a country code.
 * @param countryCode - The country code to get the currency for.
 * @returns The currency. If the country code is not found, it will return 'USD'.
 */
export const getCurrencyFromCountryCode: (countryCode: string) => string = (
    countryCode,
) => {
    return (
        countryCodeToCurrency[
            countryCode as keyof typeof countryCodeToCurrency
        ] ?? 'USD'
    );
};

const parseAmount = (amount: number | string, items: number = 1): number => {
    const numericAmount =
        typeof amount === 'number' ? amount : parsePriceWithAmount(amount);
    return numericAmount * items;
};

const formatIntegerPart = (
    integerPart: string,
    thousandSeparator: string,
    includeThousandSeparator: boolean,
): string => {
    if (!includeThousandSeparator || integerPart.length <= 3) {
        return integerPart;
    }
    // Holy regex, Batman! This regex is used to insert the thousand separator at the correct position.
    // \B is a word boundary, so it will not insert the separator at the start or end of the string.
    // (?=(\d{3})+) is a positive lookahead that matches one or more digits that are a multiple of 3.
    // (?!\d) is a negative lookahead that matches a digit that is not part of a group of 3 digits.
    return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
};

const formatDecimalPart = (amount: number, decimalPlaces: number): string => {
    const decimalPart = amount.toFixed(decimalPlaces).split('.')[1] || '';
    return decimalPart.length > decimalPlaces
        ? decimalPart.slice(0, decimalPlaces)
        : decimalPart;
};

const assembleCurrencyString = (
    formattedInteger: string,
    formattedDecimal: string,
    symbol: string,
    config: CurrencyConfig,
    includeSymbol: boolean,
): string => {
    const displaySymbol = !includeSymbol ? '' : symbol;
    const formattedAmount = formattedDecimal
        ? `${formattedInteger}${config.decimalSeparator}${formattedDecimal}`
        : formattedInteger;

    return config.after
        ? `${formattedAmount}${displaySymbol ? ` ${displaySymbol}` : ''}`
        : `${displaySymbol}${formattedAmount}`;
};

/**
 * Format a currency amount.
 * @param options - The options for the format.
 * @param options.countryCode - The country code to get the currency for. Note that this is important for the thousand and decimal separator.
 * @param options.amount - The amount to format.
 * @param options.currency - The currency to format. Falls back to the country code if not provided.
 * @param options.items - The number of items to format. This is simply multiplied with the amount.
 * @param options.includeThousandSeparator - Whether to include a thousand separator.
 * @param options.includeSymbol - Whether to include a currency symbol.
 * @returns The formatted currency amount.
 */
export const formatCurrency: (options: {
    countryCode?: string;
    amount?: number | string;
    currency?: string;
    items?: number;
    includeThousandSeparator?: boolean;
    includeSymbol?: boolean;
}) => string = (options) => {
    const {
        amount,
        currency = getCurrencyFromCountryCode(options.countryCode ?? 'USD'),
        items = 1,
        includeThousandSeparator = true,
        includeSymbol = true,
    } = options;

    const config = getCurrencyConfig(currency);
    if (!amount) return config.symbol;

    const parsedAmount = parseAmount(amount, items);
    const integerPart = Math.floor(parsedAmount).toString();

    const formattedInteger = formatIntegerPart(
        integerPart,
        config.thousandSeparator,
        includeThousandSeparator,
    );

    const formattedDecimal = formatDecimalPart(
        parsedAmount,
        config.decimalPlaces,
    );

    return assembleCurrencyString(
        formattedInteger,
        formattedDecimal,
        config.symbol,
        config,
        includeSymbol,
    );
};

/**
 * Check if the currency is after the price.
 * @param currency - The currency to check.
 * @returns Whether the currency is after the price.
 */
export const currencyAfterPrice: (currency: string) => boolean = (
    currency: string,
) => {
    const config = getCurrencyConfig(currency);
    return !!config?.after;
};

/**
 * Get the currency symbol for a given currency.
 * @param currency - The currency to get the symbol for.
 * @returns The currency symbol.
 */
export const getCurrencySymbol: (currency?: string) => string = (
    currency = 'USD',
) => {
    const config = getCurrencyConfig(currency);
    return config.symbol;
};

/**
 * Get the number of decimal places for a given currency.
 * @param currency - The currency to get the number of decimal places for.
 * @returns The number of decimal places.
 */
export const getCurrencyDecimalPlaces: (currency: string) => number = (
    currency: string,
) => {
    const config = getCurrencyConfig(currency);
    return config.decimalPlaces;
};

export const createOrderDetailsPriceCurrency = (
    o: { amount: string; currencyCode: string } | undefined,
    className?: string,
    elementAfter?: React.JSX.Element,
): React.JSX.Element | undefined => {
    if (!o) return;
    const currAfterPrice = currencyAfterPrice(o.currencyCode);
    const currency = getCurrencySymbol(o.currencyCode);
    const amount = parsePriceWithAmount(o.amount);
    return (
        <span className={`price-currency ${className}`}>
            <span style={{ order: currAfterPrice ? 2 : 0 }}>{currency}</span>
            <span className="amount">{amount}</span>
            {elementAfter}
        </span>
    );
};

/**
 * Get the free shipping threshold for a given currency.
 * @param countryCode - The country code to get the free shipping threshold for.
 * @returns The free shipping threshold.
 */
export const getFreeShippingThreshold: (
    countryCode: string,
) => number | undefined = (countryCode: string) => {
    const config = getCurrencyConfig(getCurrencyFromCountryCode(countryCode));
    return config.freeShippingThreshold;
};

/**
 * Check if the currency has a hardcoded free shipping threshold.
 * @param currencyCode - The currency code to check.
 * @returns Whether the currency has a hardcoded free shipping threshold.
 */
export const hasHardcodedFreeShippingThreshold: (
    currencyCode: string,
) => boolean = (currencyCode: string) => {
    if (!currencyConfig[currencyCode]) return false;
    const config = getCurrencyConfig(currencyCode);
    return config.freeShippingThreshold !== undefined;
};

/**
 * Get the country code from a currency code.
 * @param currencyCode - The currency code to get the country code for.
 * @returns The country code.
 */
export const getCountryCodeFromCurrency: (currencyCode: string) => string = (
    currencyCode: string,
) => {
    return (
        Object.keys(countryCodeToCurrency).find(
            (key) => countryCodeToCurrency[key] === currencyCode,
        ) ?? 'US'
    );
};

/**
 * Format a subscription price with currency symbol.
 * This is specifically designed for subscription boxes where the currency symbol
 * placement varies based on the currency type.
 *
 * @param price - The price to format
 * @param currency - The currency code (e.g., 'USD', 'NOK')
 * @returns React.JSX.Element with formatted price and currency symbol
 */
export const formatSubscriptionPrice = (
    price: number,
    currency: string,
): React.JSX.Element => {
    const symbol = getCurrencySymbol(currency);

    if (currency === 'USD' || currency === 'GBP') {
        return (
            <>
                <span className="symbol">{symbol}</span>
                <span className="price">{price}</span>
            </>
        );
    }

    return (
        <>
            <span className="nor-price">{price}</span>
            <span className="nor-symbol">{symbol}</span>
        </>
    );
};

/**
 * Format a subscription price as a string with correct currency symbol placement.
 * @param price - The price to format
 * @param currency - The currency code (e.g., 'USD', 'NOK', 'EUR')
 * @returns string with formatted price and currency symbol
 */
export const formatSubscriptionPriceAsString = (
    price: number | string,
    currency: string,
): string => {
    const config = getCurrencyConfig(currency.toUpperCase());
    const symbol = config.symbol;
    const formattedPrice = typeof price === 'number' ? price.toString() : price;

    return config.after
        ? `${formattedPrice} ${symbol}` // NOK, SEK, DKK, ISK
        : `${symbol}${formattedPrice}`; // USD, EUR, GBP
};

export const getSubscriptionPriceDetails = (
    countryCode: string,
): ISubscriptionPrices => {
    const currency = getCurrencyFromCountryCode(countryCode);
    return subcriptionPrices[currency] || subcriptionPrices['USD'];
};
export interface ISubscriptionPrices {
    yearlyPrice: string;
    yearlyPricePrMonth: string;
    yearlyPricePrMonthRounded: string;
    yearlySavings: string;
    monthlyPrice: string;
}
export const subcriptionPrices: Record<string, ISubscriptionPrices> = {
    USD: {
        yearlyPrice: '$107',
        yearlyPricePrMonth: '$8.90',
        yearlyPricePrMonthRounded: '$9',
        yearlySavings: '$73',
        monthlyPrice: '$14.99',
    },
    NOK: {
        yearlyPrice: '1068 kr',
        yearlyPricePrMonth: '89 kr',
        yearlyPricePrMonthRounded: '89 kr',
        yearlySavings: '720 kr',
        monthlyPrice: '149 kr',
    },
    EUR: {
        yearlyPrice: '€107',
        yearlyPricePrMonth: '€8.90',
        yearlyPricePrMonthRounded: '€9',
        yearlySavings: '€73',
        monthlyPrice: '€14.99',
    },
    GBP: {
        yearlyPrice: '£89',
        yearlyPricePrMonth: '£7.40',
        yearlyPricePrMonthRounded: '£7',
        yearlySavings: '£91',
        monthlyPrice: '£14.99',
    },
};
export const trialDuration = 14;
